import type { Event } from '@cocoonspace/sdk-js/types/event'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'

import type { EventItem } from './use-all-events.hook'

export type EventsByMonth = {
	month: string
	events: Event[]
}

export const getFilteredEventsByMonth = (
	events: (Event | EventItem)[],
	filters: {
		type?: string | null
		search?: string | null
		guests?: string[] | null
		labelIds?: string[] | null
	},
) => {
	let eventsList = events as (Event & EventItem)[]

	if (filters.type) {
		eventsList = eventsList?.filter((event) => event.type === filters.type)
	}

	if (filters.search) {
		const lowerCaseSearchFilter = filters.search.toLowerCase()

		eventsList = eventsList?.filter((event) => {
			const includesInTitle = event.title
				?.toLowerCase()
				.includes(lowerCaseSearchFilter)

			const includesInBookingRef = event.booking_ref
				?.toLowerCase()
				.includes(lowerCaseSearchFilter)

			return includesInTitle || includesInBookingRef
		})
	}

	if (filters.guests?.length) {
		const lowerCaseGuestFilters = filters.guests.map((g) => g.toLowerCase())

		eventsList = eventsList?.filter((event) =>
			event.guests?.some((guest) => {
				const email = typeof guest === 'string' ? guest : guest.email
				return lowerCaseGuestFilters.some((filter) =>
					email?.toLowerCase().includes(filter),
				)
			}),
		)
	}

	if (filters.labelIds?.length) {
		eventsList = eventsList?.filter((event) => {
			const labels =
				(event as EventItem).originalEvent?.labels ??
				(event as Event).labels ??
				[]
			return labels.some((label) => filters.labelIds?.includes(label.id ?? ''))
		})
	}

	return eventsList.reduce((acc: EventsByMonth[], event) => {
		const startDate =
			event.start_local ?? event.startLocal ?? event.originalEvent?.start_local

		if (!startDate) {
			return acc
		}

		const month = dateTimeUtils(startDate).format('MMMM YYYY')

		const monthIndex = acc.findIndex((accMonth) => accMonth.month === month)

		if (monthIndex > -1) {
			acc[monthIndex].events.push(event as Event)
		} else {
			acc.push({
				events: [event as Event],
				month,
			})
		}

		return acc
	}, [] as EventsByMonth[])
}
